import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CurrentUserService } from '@shared/services/current-user/current-user.service';
import { firstValueFrom, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * PublicOnlyGuard - Permite acesso APENAS para usuários NÃO autenticados
 *
 * Uso: canActivate: [publicOnlyGuard]
 *
 * @description
 * Este guard impede que usuários já autenticados acessem telas públicas como login, cadastro e recuperação de senha.
 * Se o usuário já estiver logado, redireciona para a home apropriada.
 */
export const publicOnlyGuard: CanActivateFn = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const currentUserService = inject(CurrentUserService);

    try {
        // Verifica se há usuário autenticado no Firebase Auth
        const isAuth = await firstValueFrom(authService.isAuthenticated$);

        if (isAuth) {
            // Usuário já está logado - verifica se é corretor para redirecionar corretamente
            // Usa roleLevel do JWT (BROKER) ou corretor do Firestore
            const currentUser = await firstValueFrom(
                currentUserService.getCurrentUser$().pipe(take(1)),
            );

            const isCorretor =
                currentUser?.['roleLevel'] === 'BROKER' ||
                currentUser?.['corretor'] === true;
            const redirectPath = isCorretor ? '/tabs/tabelas' : '/tabs/home';

            await router.navigate([redirectPath], {
                replaceUrl: true, // Não permite voltar para login
            });

            return false;
        }

        // Usuário não autenticado - permite acesso às telas públicas
        return true;
    } catch (error) {
        console.error('[PublicOnlyGuard] Erro ao verificar autenticação:', error);
        // Em caso de erro, permite acesso (fail-soft)
        return true;
    }
};
